import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { validateArticleImport, importArticle, getCategories, getDestinations } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { 
  Upload, FileJson, AlertCircle, CheckCircle, X, Loader2, 
  FileText, Eye, Save, ChevronDown, ChevronUp
} from "lucide-react";

// Preview component for content blocks
function ContentBlockPreview({ blocks, photos = [] }) {
  if (!blocks || blocks.length === 0) {
    return <p className="text-muted-foreground italic">Aucun bloc de contenu</p>;
  }

  // Build photo URL lookup
  const getPhotoUrl = (photoId) => {
    const photo = photos.find(p => p.id === photoId);
    return photo?.cloudinary_url || photo?.url || null;
  };

  return (
    <div className="space-y-4 text-sm">
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "section_title":
            return (
              <h3 key={idx} className="font-heading text-lg font-bold text-primary border-l-4 border-accent pl-3">
                {block.text}
              </h3>
            );
          case "paragraph":
            return (
              <p key={idx} className="text-foreground leading-relaxed">
                {block.text?.substring(0, 300)}{block.text?.length > 300 ? "..." : ""}
              </p>
            );
          case "quote":
            return (
              <blockquote key={idx} className="border-l-4 border-muted pl-4 italic text-muted-foreground">
                {block.text}
              </blockquote>
            );
          case "image":
            return (
              <div key={idx} className="bg-muted/30 p-3 rounded-lg flex items-center gap-3">
                <div className="w-16 h-16 bg-muted rounded flex items-center justify-center overflow-hidden">
                  {getPhotoUrl(block.photo_id) ? (
                    <img src={getPhotoUrl(block.photo_id)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <FileText className="h-6 w-6 text-muted-foreground/50" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Image</p>
                  <p className="text-sm font-mono truncate">{block.photo_id}</p>
                  {block.caption && <p className="text-xs italic text-muted-foreground mt-1">{block.caption}</p>}
                </div>
              </div>
            );
          case "image_gallery":
            return (
              <div key={idx} className="bg-muted/30 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Galerie ({block.photo_ids?.length || 0} photos)</p>
                <div className="flex gap-2 flex-wrap">
                  {block.photo_ids?.map((pid, i) => (
                    <div key={i} className="w-12 h-12 bg-muted rounded flex items-center justify-center overflow-hidden">
                      {getPhotoUrl(pid) ? (
                        <img src={getPhotoUrl(pid)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs font-mono">{pid.substring(0, 6)}</span>
                      )}
                    </div>
                  ))}
                </div>
                {block.caption && <p className="text-xs italic text-muted-foreground mt-2">{block.caption}</p>}
              </div>
            );
          default:
            return (
              <div key={idx} className="bg-red-50 p-2 rounded text-red-600 text-xs">
                Type inconnu : {block.type}
              </div>
            );
        }
      })}
    </div>
  );
}

export default function ArticleImporter() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // States
  const [jsonInput, setJsonInput] = useState("");
  const [parsedData, setParsedData] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [categories, setCategories] = useState([]);
  const [destinations, setDestinations] = useState([]);

  // Load categories and destinations on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [catsRes, destsRes] = await Promise.all([
          getCategories(),
          getDestinations()
        ]);
        setCategories(catsRes.data);
        setDestinations(destsRes.data);
      } catch (err) {
        console.error("Failed to load categories/destinations:", err);
      }
    };
    loadData();
  }, []);

  // Parse JSON input
  const parseJson = useCallback((text) => {
    setParseError(null);
    setParsedData(null);
    setValidationResult(null);
    
    if (!text.trim()) {
      return;
    }

    try {
      const data = JSON.parse(text);
      
      // Basic structure validation
      if (!data.title) {
        setParseError("Le champ 'title' est requis");
        return;
      }
      if (!data.category_id) {
        setParseError("Le champ 'category_id' est requis");
        return;
      }
      if (!data.content_blocks || !Array.isArray(data.content_blocks)) {
        setParseError("Le champ 'content_blocks' est requis et doit être un tableau");
        return;
      }
      
      setParsedData(data);
    } catch (e) {
      setParseError(`JSON invalide : ${e.message}`);
    }
  }, []);

  // Handle text input change
  const handleJsonChange = (e) => {
    const text = e.target.value;
    setJsonInput(text);
    parseJson(text);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith(".json")) {
      toast.error("Veuillez sélectionner un fichier .json");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === "string") {
        setJsonInput(text);
        parseJson(text);
      }
    };
    reader.onerror = () => {
      toast.error("Erreur lors de la lecture du fichier");
    };
    reader.readAsText(file);
  };

  // Validate with backend
  const handleValidate = async () => {
    if (!parsedData) {
      toast.error("Aucun JSON valide à valider");
      return;
    }
    
    setValidating(true);
    setValidationResult(null);
    
    try {
      const res = await validateArticleImport(parsedData);
      setValidationResult(res.data);
      
      if (res.data.valid) {
        toast.success(`Validation réussie ! ${res.data.word_count} mots, ${res.data.block_count} blocs`);
      } else {
        toast.error(`Validation échouée : ${res.data.errors.length} erreur(s)`);
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Erreur de validation";
      toast.error(message);
      setValidationResult({
        valid: false,
        errors: [message],
        warnings: [],
        word_count: 0,
        block_count: 0,
        photo_ids_found: [],
        photo_ids_missing: []
      });
    } finally {
      setValidating(false);
    }
  };

  // Import article
  const handleImport = async () => {
    if (!parsedData) {
      toast.error("Aucun JSON à importer");
      return;
    }
    
    if (!validationResult?.valid) {
      toast.error("Veuillez d'abord valider le JSON");
      return;
    }
    
    setImporting(true);
    
    try {
      const res = await importArticle(parsedData);
      
      if (res.data.success) {
        toast.success(res.data.message);
        // Navigate to article edit page
        navigate(`/article/${res.data.article_id}/edit`);
      } else {
        toast.error("Échec de l'import");
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Erreur lors de l'import";
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  // Get category/destination names for display
  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || id;
  const getDestinationName = (id) => destinations.find(d => d.id === id)?.name || id;

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="article-importer">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-2">
            Importer un article JSON
          </h1>
          <p className="text-muted-foreground">
            Importez un article préparé en externe au format JSON structuré.
            L&apos;article sera créé en <span className="font-semibold text-amber-600">brouillon</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Input */}
          <div className="space-y-4">
            <div className="bg-white border border-border rounded-lg p-6">
              <h2 className="font-heading text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                <FileJson className="h-5 w-5 text-accent" />
                Source JSON
              </h2>
              
              {/* File upload */}
              <div className="mb-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".json"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full gap-2"
                  data-testid="upload-json-btn"
                >
                  <Upload className="h-4 w-4" />
                  Charger un fichier .json
                </Button>
              </div>
              
              <div className="relative mb-1">
                <span className="absolute left-1/2 -translate-x-1/2 bg-white px-2 text-xs text-muted-foreground">
                  ou coller le JSON
                </span>
                <hr className="border-border" />
              </div>
              
              {/* Text input */}
              <Textarea
                value={jsonInput}
                onChange={handleJsonChange}
                placeholder={`{
  "title": "Mon article",
  "category_id": "xxx-xxx",
  "content_blocks": [
    {"type": "paragraph", "text": "..."},
    {"type": "section_title", "text": "..."},
    {"type": "image", "photo_id": "xxx", "caption": "..."}
  ]
}`}
                className="font-mono text-sm h-64 resize-none"
                data-testid="json-input"
              />
              
              {/* Parse error */}
              {parseError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{parseError}</p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={handleValidate}
                  disabled={!parsedData || validating}
                  className="flex-1 gap-2"
                  variant="outline"
                  data-testid="validate-btn"
                >
                  {validating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                  Valider
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={!validationResult?.valid || importing}
                  className="flex-1 gap-2 bg-accent hover:bg-accent/90"
                  data-testid="import-btn"
                >
                  {importing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Importer en brouillon
                </Button>
              </div>
            </div>

            {/* Validation Result */}
            {validationResult && (
              <div 
                className={`border rounded-lg p-4 ${
                  validationResult.valid 
                    ? "bg-green-50 border-green-200" 
                    : "bg-red-50 border-red-200"
                }`}
                data-testid="validation-result"
              >
                <div className="flex items-center gap-2 mb-3">
                  {validationResult.valid ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  )}
                  <h3 className={`font-semibold ${validationResult.valid ? "text-green-800" : "text-red-800"}`}>
                    {validationResult.valid ? "Validation réussie" : "Validation échouée"}
                  </h3>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                  <div className="bg-white/50 rounded p-2">
                    <span className="text-muted-foreground">Mots :</span>{" "}
                    <span className="font-semibold">{validationResult.word_count}</span>
                  </div>
                  <div className="bg-white/50 rounded p-2">
                    <span className="text-muted-foreground">Blocs :</span>{" "}
                    <span className="font-semibold">{validationResult.block_count}</span>
                  </div>
                  {validationResult.category_name && (
                    <div className="bg-white/50 rounded p-2">
                      <span className="text-muted-foreground">Catégorie :</span>{" "}
                      <span className="font-semibold">{validationResult.category_name}</span>
                    </div>
                  )}
                  {validationResult.destination_name && (
                    <div className="bg-white/50 rounded p-2">
                      <span className="text-muted-foreground">Destination :</span>{" "}
                      <span className="font-semibold">{validationResult.destination_name}</span>
                    </div>
                  )}
                </div>

                {/* Photos */}
                {(validationResult.photo_ids_found.length > 0 || validationResult.photo_ids_missing.length > 0) && (
                  <div className="mb-3 text-sm">
                    {validationResult.photo_ids_found.length > 0 && (
                      <p className="text-green-700">
                        {validationResult.photo_ids_found.length} photo(s) trouvée(s)
                      </p>
                    )}
                    {validationResult.photo_ids_missing.length > 0 && (
                      <p className="text-red-700">
                        {validationResult.photo_ids_missing.length} photo(s) manquante(s)
                      </p>
                    )}
                  </div>
                )}

                {/* Errors */}
                {validationResult.errors.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-red-800 mb-1">Erreurs :</p>
                    <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                      {validationResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {validationResult.warnings.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-amber-800 mb-1">Avertissements :</p>
                    <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                      {validationResult.warnings.map((warn, i) => (
                        <li key={i}>{warn}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Preview */}
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="w-full p-4 flex items-center justify-between bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <h2 className="font-heading text-lg font-semibold text-primary flex items-center gap-2">
                <Eye className="h-5 w-5 text-accent" />
                Aperçu
              </h2>
              {showPreview ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>

            {showPreview && (
              <div className="p-6 max-h-[70vh] overflow-y-auto" data-testid="preview-panel">
                {parsedData ? (
                  <div className="space-y-6">
                    {/* Title */}
                    <div>
                      <h1 className="font-heading text-2xl font-bold text-primary">
                        {parsedData.title}
                      </h1>
                      {parsedData.subtitle && (
                        <p className="text-lg text-muted-foreground mt-1">{parsedData.subtitle}</p>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="flex flex-wrap gap-2 text-xs">
                      {parsedData.category_id && (
                        <span className="px-2 py-1 bg-accent/10 text-accent rounded">
                          {getCategoryName(parsedData.category_id)}
                        </span>
                      )}
                      {parsedData.destination_id && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {getDestinationName(parsedData.destination_id)}
                        </span>
                      )}
                      {parsedData.venue_name && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                          {parsedData.venue_name}
                        </span>
                      )}
                      {parsedData.dates && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                          {parsedData.dates}
                        </span>
                      )}
                    </div>

                    {/* Excerpt */}
                    {parsedData.excerpt && (
                      <p className="text-muted-foreground italic border-l-4 border-accent pl-4">
                        {parsedData.excerpt}
                      </p>
                    )}

                    {/* Content blocks */}
                    <div className="border-t pt-4">
                      <p className="text-xs text-muted-foreground mb-3">
                        {parsedData.content_blocks?.length || 0} blocs de contenu
                      </p>
                      <ContentBlockPreview blocks={parsedData.content_blocks} />
                    </div>

                    {/* Tags */}
                    {parsedData.tags && parsedData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {parsedData.tags.map((tag, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-muted rounded">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileJson className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Collez ou chargez un fichier JSON pour voir l&apos;aperçu</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* JSON Schema Help */}
        <details className="mt-8 bg-white border border-border rounded-lg">
          <summary className="p-4 cursor-pointer font-medium text-primary hover:bg-muted/30">
            Structure JSON attendue
          </summary>
          <div className="p-4 border-t bg-muted/10">
            <pre className="text-xs overflow-x-auto p-4 bg-gray-900 text-gray-100 rounded-lg">
{`{
  "title": "Titre de l'article (requis)",
  "subtitle": "Sous-titre (optionnel)",
  "excerpt": "Résumé / chapô (optionnel)",
  "category_id": "id-de-la-categorie (requis)",
  "destination_id": "id-de-la-destination (optionnel)",
  "cover_photo_id": "id-photo-couverture (optionnel)",
  "content_blocks": [
    { "type": "paragraph", "text": "Premier paragraphe..." },
    { "type": "section_title", "text": "Titre de section" },
    { "type": "paragraph", "text": "Contenu de la section..." },
    { "type": "image", "photo_id": "xxx-xxx", "caption": "Légende" },
    { "type": "image_gallery", "photo_ids": ["id1", "id2", "id3"], "caption": "Légende commune" },
    { "type": "quote", "text": "Une citation inspirante" }
  ],
  "tags": ["voyage", "croisière", "méditerranée"],
  "seo_title": "Titre SEO (max 60 caractères)",
  "meta_description": "Description meta (max 160 caractères)",
  "venue_name": "Nom du lieu (hôtel, navire, restaurant)",
  "company": "Compagnie (ex: MSC Croisières)",
  "dates": "15-22 mars 2024"
}`}
            </pre>
            <div className="mt-4 text-sm text-muted-foreground">
              <p className="font-medium mb-2">Types de blocs :</p>
              <ul className="list-disc list-inside space-y-1">
                <li><code className="bg-muted px-1 rounded">section_title</code> - Titre de section (h2)</li>
                <li><code className="bg-muted px-1 rounded">paragraph</code> - Paragraphe de texte</li>
                <li><code className="bg-muted px-1 rounded">quote</code> - Citation</li>
                <li><code className="bg-muted px-1 rounded">image</code> - Photo unique (photo_id requis)</li>
                <li><code className="bg-muted px-1 rounded">image_gallery</code> - Galerie (min 2 photo_ids)</li>
              </ul>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
